import { defineStore } from "pinia";
import { checkFlow, getOnboardingFlow } from "@/constants/onboarding";
import { QUIZ_IDS } from "@/constants/onboarding/quizIds";
import { fetchPaddleProducts } from "@/helpers/paddle";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { getOnboardingFlowNameFromRoute } from "@/helpers/page";
import { getPaddleDiscount } from "@/services/paddleService";

export const useOnboardingStore = defineStore("onboarding", () => {
  // State
  const initialized = ref(false);
  const products = ref([]);

  const route = useRoute();
  const QUIZ_ID = computed(() => {
    const routeParam = getOnboardingFlowNameFromRoute(route) || QUIZ_IDS.default
    const oid = routeParam.replace('-', '');
    return checkFlow(oid) ? oid : QUIZ_IDS.default;
  });

  const discount = ref(null);

  const isSpecialOffer = computed(() => !!rawFlow.value?.discounts?.special );
  const isSpecialOfferActivated = ref(false);

  // Get raw flow configuration
  const rawFlow = computed(() => getOnboardingFlow(QUIZ_ID.value));

  // Get flow steps
  const steps = computed(() => rawFlow.value?.steps ?? []);
  const discountRate = computed(() => (discount.value?.amount || 0) / 100); // EX: 0.6
  const discountId = computed(() => discount.value?.id);
  const prices = computed(() => rawFlow.value?.prices ?? []);

  const fetchFlowDetails = async () => {
    if (prices.value.length) {
      const fetchedProducts = await fetchPaddleProducts(prices.value);
      products.value = fetchedProducts;
      const discountId = rawFlow.value?.discounts?.default
      if (discountId) {
        await setDiscount(discountId, "default");
      }
    }
    initialized.value = true;
  };

  const setDiscount = async (discountId, source, discountCode) => {
    if (!discountId) {
      discount.value = null;
      isSpecialOfferActivated.value = false;
      return;
    }

    const discountResponse = await getPaddleDiscount(discountId);
    if (discountResponse?.data?.success) {
      const discountDetails = {
        id: discountId,
        type: discountResponse.data.type,
        amount: discountResponse.data.amount,
        ...(discountResponse.data.type !== "percentage" && {
          currency_code: discountResponse.currency_code,
        }),
        ...(source === "checkout" && { code: discountCode }),
      };
      discount.value = discountDetails;
    }
  };

  const claimSpecialDiscount = async () => {
    if (!isSpecialOffer.value) return;
    let discountId = rawFlow.value?.discounts?.special;
    await setDiscount(discountId, "default", null);
    setIsSpecialOfferActivated(true);
  };

  const setIsSpecialOfferActivated = (value) => {
    if (!isSpecialOffer.value) return;
    isSpecialOfferActivated.value = value;
  };


  // Watch for changes in flow configuration to update products and discount
  watch(
    rawFlow,
    async () => {
      if (import.meta.client) {
        initialized.value = false;
        await fetchFlowDetails();
      }
    },
    { immediate: true },
  );

  return {
    initialized,
    QUIZ_ID,
    steps,
    discountId,
    prices,
    discountRate,
    products,
    discount,
    isSpecialOffer,
    isSpecialOfferActivated,
    fetchFlowDetails,
    setDiscount,
    claimSpecialDiscount,
    setIsSpecialOfferActivated,
  };
});
